import React from 'react';
import { useTranslation } from 'react-i18next';
import { LatLngBounds, LatLng } from 'leaflet';
import {
  getCountryByName,
  getCountryNameByLang,
  getTranslatedRiskClassValue,
  getTranslatedValueForPolicy, 
} from '../../utils/utils';
import {
  MENU_TYPE_COUNTRY_DROPDOWN,
  MENU_TYPE_CONTINENT_DROPDOWN,
  MENU_TYPE_RISK_CLASS_DROPDOWN,
  MENU_TYPE_POLICY_DROPDOWN,
  RISK_CLASS_NONE,
} from '../../utils/variables';
import { FilterQuery } from '../../utils/filters';
import { Country } from '../../types/Country';

type Props = {
  state: Country[];
  menuItems: string[];
  menuType: string;
  query: FilterQuery;
  setQuery: (query: FilterQuery) => void;
  setCenter: (center: LatLng) => void;
}

const initialQuery: FilterQuery = {
  selectedCountry: '',
  riskClass: '',
  continent: '',
  coverPolicy: '',
};

const FilterDropdown: React.FunctionComponent<Props> = (props: Props) => {
  const { t } = useTranslation();
  const { state, menuItems, menuType, query, setQuery, setCenter } = props;

  const SELECT_COUNTRY = t('filters:searchCountry');
  const SELECT_CONTINENT = t('filters:selectContinent');
  const SELECT_RISK_CLASS = t("filters:selectRiskClass");
  const SELECT_COVER_POLICY = t("filters:selectPolicy");

  const handleSelect = (event: React.FormEvent<HTMLSelectElement>) => {
    event.preventDefault();
    const value = event.currentTarget.value;
    switch (menuType) {
      case MENU_TYPE_COUNTRY_DROPDOWN: {
        const country = getCountryByName(state, value);
        if (country) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const bounds = new LatLngBounds(country.geoJson.geometry.coordinates);
          // For some reason lat is lng and other way around.
          let center = bounds.getCenter();
          center = new LatLng(center.lng, center.lat);
          setCenter(center);
          const newQuery = {... initialQuery};
          newQuery.selectedCountry = country;
          newQuery.continent = '';
          setQuery(newQuery);
        }
        else {
          const newQuery = {... initialQuery};
          setQuery(newQuery);
        }
        break;
      }
      case MENU_TYPE_CONTINENT_DROPDOWN: {
        const newQuery = {...query};
        newQuery.continent = value;
        newQuery.selectedCountry = '';
        setQuery(newQuery);
        break;
      }
      case MENU_TYPE_RISK_CLASS_DROPDOWN: {
        const newQuery = {...query};
        newQuery.riskClass = value;
        newQuery.selectedCountry = '';
        setQuery(newQuery);
        break;
      }
      case MENU_TYPE_POLICY_DROPDOWN: {
        const newQuery = {...query};
        newQuery.coverPolicy = value;
        newQuery.selectedCountry = '';
        setQuery(newQuery);
        break;
      }
      default: {
        break;
      }
    }
  }

  const renderOptions = (options: any[]) => {
    return options.map((option, index) => {
      let valueAndExplanation = option;
      if (menuType === MENU_TYPE_RISK_CLASS_DROPDOWN) {
        const explanation = getTranslatedRiskClassValue(option);
        if (explanation) {
          if (option === RISK_CLASS_NONE) {
            valueAndExplanation = t(explanation);
          }
          else {
            valueAndExplanation = valueAndExplanation + ' - ' + t(explanation);
          }
        }
        return <option id={option} key={index} value={option}>{valueAndExplanation}</option>
      }
      else if (menuType === MENU_TYPE_POLICY_DROPDOWN) {
        const explanation = getTranslatedValueForPolicy(option);
        if (explanation) {
          if (option === RISK_CLASS_NONE) {
            valueAndExplanation = t(explanation);
          }
          else {
            valueAndExplanation = valueAndExplanation + ' - ' + t(explanation);
          }
        }
        return <option id={option} key={index} value={option}>{valueAndExplanation}</option>
      }
      else {
        return <option id={option} key={index} value={option}>{option}</option>
      }
    });
  }

  const renderHiddenLabel = () => {
    switch (menuType) {
      case MENU_TYPE_COUNTRY_DROPDOWN: {
        return SELECT_COUNTRY;
      }
      case MENU_TYPE_CONTINENT_DROPDOWN: {
        return SELECT_CONTINENT;
      }
      case MENU_TYPE_RISK_CLASS_DROPDOWN: {
        return SELECT_RISK_CLASS;
      }
      case MENU_TYPE_POLICY_DROPDOWN: {
        return SELECT_COVER_POLICY;
      }
      default: {
        return '';
      }
    }
  }

  const getValue = (menuType: string) => {
    switch (menuType) {
      case MENU_TYPE_COUNTRY_DROPDOWN: {
        return query.selectedCountry ? getCountryNameByLang(query.selectedCountry) : SELECT_COUNTRY;
      }
      case MENU_TYPE_CONTINENT_DROPDOWN: {
        return query.selectedCountry ? SELECT_CONTINENT : query.continent;
      }
      case MENU_TYPE_RISK_CLASS_DROPDOWN: {
        return query.selectedCountry ? SELECT_RISK_CLASS : query.riskClass;
      }
      case MENU_TYPE_POLICY_DROPDOWN: {
        return query.selectedCountry ? SELECT_COVER_POLICY : query.coverPolicy;
      }
      default: {
        return '';
      }
    }
  }

  const renderDefaultOption = (menuType: string) => {
    switch (menuType) {
      case MENU_TYPE_COUNTRY_DROPDOWN: {
        return <option id={menuType} value={''}>{SELECT_COUNTRY}</option>
      }
      case MENU_TYPE_CONTINENT_DROPDOWN: {
        return <option id={menuType} value={''}>{SELECT_CONTINENT}</option>
      }
      case MENU_TYPE_RISK_CLASS_DROPDOWN: {
        return <option id={menuType} value={''}>{SELECT_RISK_CLASS}</option>
      }
      case MENU_TYPE_POLICY_DROPDOWN: {
        return <option id={menuType} value={''}>{SELECT_COVER_POLICY}</option>
      }
      default: {
        break;
      }
    }
  }

  return (
    <div className="filter-dropdown-menu">
      <label hidden>{renderHiddenLabel()}</label>
      <select className="filter-dropdown-select" value={getValue(menuType)} onChange={handleSelect}>
        {renderDefaultOption(menuType)}
        {renderOptions(menuItems)}
      </select>
    </div>
  );
}

export default FilterDropdown;